import type {EndpointDirectoryV3, HeaderV1} from '@smart/endpoint-directory';
import type {LocaleV3} from '@smart/locale-service';
import type {
  LeasingParamsV1,
  PurchaseItemV1,
} from '@smart/payment-config-service';
import type {PriceTypeV1} from '@smart/price-preferences-provider';
import {isNullish} from '@smart/utils';
import type {PriceDetailsByType} from '../../bff/price-details';

export interface ContractHireParams {
  readonly advanceMonth: number;
  readonly duration: number;
  readonly mileage: number;
}
export interface FetchPriceDetailsOptions {
  readonly endpointDirectory?: EndpointDirectoryV3;
  readonly locale?: LocaleV3;
  readonly purchaseItem: PurchaseItemV1;
  readonly params: LeasingParamsV1 | ContractHireParams;
  readonly signal: AbortSignal;
  readonly priceType: PriceTypeV1;
  readonly enableAnonymousFlow: boolean;
}

const hasAdvanceMonth = (
  params: LeasingParamsV1 | ContractHireParams,
): params is ContractHireParams => `advanceMonth` in params;

const DEFAULT_ERROR_MESSAGE = `Could not fetch price details.`;

export async function fetchPriceDetails(
  baseUrl: string,
  options: FetchPriceDetailsOptions,
): Promise<PriceDetailsByType> {
  const {
    endpointDirectory,
    locale,
    purchaseItem,
    params,
    priceType,
    signal,
    enableAnonymousFlow,
  } = options;

  const {envName, preview, rotatingVerifySecret} = endpointDirectory ?? {
    envName: ``,
    preview: ``,
    rotatingVerifySecret: ``,
  };
  const {marketId, languageTag} = locale ?? {
    marketId: ``,
    languageTag: ``,
  };

  if (!endpointDirectory) {
    throw Error(DEFAULT_ERROR_MESSAGE);
  }

  if (isNullish(params?.duration) && isNullish(params?.mileage)) {
    if (envName === `prod`) throw Error(DEFAULT_ERROR_MESSAGE);
    throw Error(
      `${DEFAULT_ERROR_MESSAGE} "duration": ${params?.duration}, "mileage": ${params?.mileage}`,
    );
  }

  const searchParams = new URLSearchParams({
    envName,
    preview: String(preview),
    marketId,
    languageTag,
    duration: params.duration.toString(),
    mileage: params.mileage.toString(),
    enableAnonymousFlow: String(enableAnonymousFlow),
    priceType,
  });

  if (hasAdvanceMonth(params)) {
    if (!isNullish(params?.advanceMonth)) {
      searchParams.set(`advanceMonth`, params.advanceMonth.toString());
    }
  } else {
    if (!isNullish(params?.advancePayment)) {
      searchParams.set(`advancePayment`, params.advancePayment.toString());
    }
  }

  if (purchaseItem.type === `pn18`) {
    searchParams.set(`pn18`, purchaseItem.pn18);
  } else {
    searchParams.set(
      `cartEntryNumber`,
      // @ts-ignore
      purchaseItem.cartEntryNumber.toString(),
    );
  }

  if (rotatingVerifySecret) {
    const {headerName, headerValue} = rotatingVerifySecret as HeaderV1;

    searchParams.set(`rotatingVerifySecret`, `${headerName}:${headerValue}`);
  }
  const url =
    purchaseItem.type === `pn18`
      ? `${baseUrl}/bff/price-details?${searchParams.toString()}`
      : `${baseUrl.replace(
          `__app__`,
          `__app-private__`,
        )}/bff/cart-price-details?${searchParams.toString()}`;

  const response = await fetch(url, {signal});

  if (!response.ok) {
    throw Error(DEFAULT_ERROR_MESSAGE);
  }

  return response.json();
}
